import React, { useEffect, useState, useCallback } from 'react';
import { useUser } from './User';
import {
  getTaxExemptRequests,
  approveTaxExemptRequest,
  rejectTaxExemptRequest,
  getAvalaraExemptions,
} from '../libs/db-lib';
import { TaxExemptRequest } from '../types';

export interface TaxExemptRequestsInterface {
  areTaxExemptRequestsLoading: boolean;
  taxExemptRequests: TaxExemptRequest[] | null;
  exemptionReasons: { id: string; name: string }[];
  // Filter state exposed for dashboard to control
  taxExemptStatus: string | null;
  taxExemptStartDate: string;
  taxExemptEndDate: string;
  setTaxExemptStatus: (status: string | null) => void;
  setTaxExemptDateRange: (startDate: string, endDate: string) => void;
  handleRefreshTaxExempt(startDate?: string, endDate?: string): any;
  handleApproveRequest(teRequestID: string, userID: string, exemptionReason: string, reasonID: string, zendeskTicketID: string, skipRefunds: boolean): any;
  handleRejectRequest(teRequestID: string, userID: string, reason: string, zendeskTicketID: string): any;
}

// Calculate default dates (last 30 days)
const getDefaultDateRange = (): { startDate: string; endDate: string } => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);

  const formatDate = (date: Date) => date.toISOString().split('T')[0];
  return {
    startDate: formatDate(startDate)+'T00:00:00.000Z',
    endDate: formatDate(endDate)+'T23:59:59.999Z',
  };
};

const defaultDates = getDefaultDateRange();

export const TaxExemptRequestsContext = React.createContext<TaxExemptRequestsInterface>({
  areTaxExemptRequestsLoading: true,
  taxExemptRequests: null,
  exemptionReasons: [],
  taxExemptStatus: 'PENDING',
  taxExemptStartDate: defaultDates.startDate,
  taxExemptEndDate: defaultDates.endDate,
  setTaxExemptStatus: () => null,
  setTaxExemptDateRange: () => null,
  handleRefreshTaxExempt: () => null,
  handleApproveRequest: (teRequestID, userID, exemptionReason) => null,
  handleRejectRequest: (teRequestID, userID, reason) => null,
});

export const useTaxExemptRequests = () => {
  return React.useContext(TaxExemptRequestsContext);
};

type TaxExemptRequestsProviderType = {
  children: JSX.Element;
};

export function TaxExemptRequestsProvider({ children }: TaxExemptRequestsProviderType) {
  const { user } = useUser();

  const [areTaxExemptRequestsLoading, setAreTaxExemptRequestsLoading] = useState<boolean>(true);
  const [taxExemptRequests, setTaxExemptRequests] = useState<TaxExemptRequest[]>([]);
  const [exemptionReasons, setExemptionReasons] = useState<{ id: string; name: string }[]>([]);

  // Filter state with defaults: PENDING status and last 30 days
  const [taxExemptStatus, setTaxExemptStatus] = useState<string | null>('PENDING');
  const [taxExemptStartDate, setTaxExemptStartDate] = useState<string>(defaultDates.startDate);
  const [taxExemptEndDate, setTaxExemptEndDate] = useState<string>(defaultDates.endDate);

  const setTaxExemptDateRange = (startDate: string, endDate: string) => {
    setTaxExemptStartDate(startDate);
    setTaxExemptEndDate(endDate);
  };

  // Fetch exemption reasons
  const fetchExemptionReasons = async () => {
    const response = await getAvalaraExemptions();
    if (!response.error) {
      setExemptionReasons(response);
    }
  };

  const handleRefreshTaxExempt = async (startDate?: string, endDate?: string) => {
    if (!user || (user && !['ADMIN'].includes(user.userType))) {
      return;
    }

    const effectiveStartDate = startDate ?? taxExemptStartDate;
    const effectiveEndDate = endDate ?? taxExemptEndDate;

    setAreTaxExemptRequestsLoading(true);
    const response = await getTaxExemptRequests(taxExemptStatus, effectiveStartDate, effectiveEndDate);

    if (!response.error) {
      setTaxExemptRequests(response.records);
      if (startDate) {
        setTaxExemptStartDate(startDate);
      }
      if (endDate) {
        setTaxExemptEndDate(endDate);
      }
    } else {
      console.log("Error refreshing tax exempt requests: ", response.error);
    }

    if (exemptionReasons.length === 0) {
      await fetchExemptionReasons();
    }

    setAreTaxExemptRequestsLoading(false);
    return response;
  }

  // Interval refresh every 5 minutes
  useEffect(() => {
    handleRefreshTaxExempt();
    if (exemptionReasons.length === 0) {
      fetchExemptionReasons();
    }

    const refreshInterval = setInterval(() => {
      handleRefreshTaxExempt();
    }, 300000); // 5 minutes

    return () => {
      clearInterval(refreshInterval);
    };
  }, [user]);

  const handleApproveRequest = async (teRequestID: string, userID: string, reason: string, reasonID: string, zendeskTicketID: string, skipRefunds: boolean) => {
    if (!user || (user && !['ADMIN'].includes(user.userType))) {
      return;
    }

    const response = await approveTaxExemptRequest(teRequestID, userID, reason, reasonID, zendeskTicketID, skipRefunds);

    if (!response.error) {
      setTaxExemptRequests(prev => prev.map((request: TaxExemptRequest) => {
        if (request.teRequestID !== teRequestID) {
          return request;
        } else {
          request.status = "APPROVED";
          request.exemptionReason = response;
          return request;
        }
      }));
    }

    return response;
  };

  const handleRejectRequest = async (teRequestID: string, userID: string, reason: string, zendeskTicketID: string) => {
    if (!user || (user && !['ADMIN'].includes(user.userType))) {
      return;
    }

    const response = await rejectTaxExemptRequest(teRequestID, userID, reason, zendeskTicketID);

    if (!response.error) {
      setTaxExemptRequests(prev => prev.map((request: TaxExemptRequest) => {
        if (request.teRequestID !== teRequestID) {
          return request;
        } else {
          request.status = "REJECTED";
          request.rejectionReason = reason;
          return request;
        }
      }));
    }

    return response;
  };

  const provider = {
    areTaxExemptRequestsLoading,
    taxExemptRequests,
    exemptionReasons,
    taxExemptStatus,
    taxExemptStartDate,
    taxExemptEndDate,
    setTaxExemptStatus,
    setTaxExemptDateRange,
    handleRefreshTaxExempt,
    handleApproveRequest,
    handleRejectRequest,
  };

  return <TaxExemptRequestsContext.Provider value={provider}>{children}</TaxExemptRequestsContext.Provider>;
}